import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, Clock, RefreshCw, Eye, EyeOff, Maximize2, Minimize2 } from 'lucide-react';

import { 
  TimerControls, 
  PeriodControls, 
  ScoreControls, 
  CardsPanel, 
  EventsPanel, 
  SettingsPanel,
  GraphicsPanel
} from '../components/control';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPORT_CONFIG = {
  soccer: { name: 'Soccer', hasCombinedScore: false },
  rugby: { name: 'Rugby', hasCombinedScore: false },
  gaelic_football: { name: 'Gaelic Football', hasCombinedScore: true },
  hurling: { name: 'Hurling / Camogie', hasCombinedScore: true }
};

const Control = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  
  // State
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [localTime, setLocalTime] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [lineupStatus, setLineupStatus] = useState(null);
  const [matchEvents, setMatchEvents] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [previewExpanded, setPreviewExpanded] = useState(false);
  // Period start times - varies by sport (set after game is loaded)
  const [periodStartTimes, setPeriodStartTimes] = useState({
    '1st_half': '0:00',
    '2nd_half': '45:00',
    '1st_et': '90:00',
    '2nd_et': '105:00',
    'fulltime': '90:00'
  });
  
  const timerRef = useRef(null);
  const pollRef = useRef(null);
  const previewRef = useRef(null);
  const clockOffsetRef = useRef(0); // serverTime - clientTime (ms)

  // Sync client/server clock offset so the displayed time is independent
  // of how badly the local PC clock might be set.
  const syncClockOffset = useCallback(async () => {
    try {
      const t0 = Date.now();
      const res = await axios.get(`${API}/server-time`);
      const t1 = Date.now();
      const serverNow = new Date(res.data.server_time).getTime();
      clockOffsetRef.current = serverNow + (t1 - t0) / 2 - t1;
    } catch (e) {
      console.warn('Clock offset sync failed (will retry):', e?.message || e);
    }
  }, []);

  useEffect(() => {
    syncClockOffset();
    const id = setInterval(syncClockOffset, 30000);
    const onVis = () => { if (!document.hidden) syncClockOffset(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [syncClockOffset]);

  // Update period start times based on sport
  useEffect(() => {
    if (game) {
      const sport = game.sport || 'soccer';
      if (sport === 'gaelic_football' || sport === 'hurling') {
        // GAA: 30 min halves, 10 min ET halves
        setPeriodStartTimes({
          '1st_half': '0:00',
          '2nd_half': '30:00',
          '1st_et': '60:00',
          '2nd_et': '70:00',
          'fulltime': '60:00'
        });
      } else if (sport === 'rugby') {
        // Rugby: 40 min halves, 10 min ET halves
        setPeriodStartTimes({
          '1st_half': '0:00',
          '2nd_half': '40:00',
          '1st_et': '80:00',
          '2nd_et': '90:00',
          'fulltime': '80:00'
        });
      } else {
        // Soccer: 45 min halves, 15 min ET halves
        setPeriodStartTimes({
          '1st_half': '0:00',
          '2nd_half': '45:00',
          '1st_et': '90:00',
          '2nd_et': '105:00',
          'fulltime': '90:00'
        });
      }
    }
  }, [game?.sport]);

  // Data fetching
  const fetchGame = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/games/${gameId}`, {
        headers: getAuthHeaders()
      });
      const serverGame = response.data;
      setGame(serverGame);
      
      // Only sync time from server if timer is NOT running locally
      // This prevents jumps during active timing
      if (!serverGame.is_running) {
        setLocalTime(serverGame.current_time);
      }
      return serverGame;
    } catch (error) {
      toast.error('Failed to load game');
      navigate('/');
      return null;
    } finally {
      setLoading(false);
    }
  }, [gameId, getAuthHeaders, navigate]);

  const fetchLineupStatus = async () => {
    try {
      const response = await axios.get(`${API}/games/${gameId}/lineups`, {
        headers: getAuthHeaders()
      });
      setLineupStatus(response.data);
    } catch (error) {
      setLineupStatus(null);
    }
  };

  const fetchMatchEvents = async () => {
    try {
      const response = await axios.get(`${API}/games/${gameId}/match-events`, {
        headers: getAuthHeaders()
      });
      setMatchEvents(response.data.events || []);
    } catch (error) {
      console.error('Failed to fetch match events');
    }
  };

  // Initial load + WebSocket subscription + fallback polling
  useEffect(() => {
    fetchGame();
    fetchLineupStatus();
    fetchMatchEvents();

    // WebSocket for real-time game state updates
    let ws = null;
    let reconnectTimer = null;
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
      const wsProto = apiUrl.startsWith('https') ? 'wss' : 'ws';
      const host = apiUrl.replace(/^https?:\/\//, '');
      const wsUrl = `${wsProto}://${host}/api/ws/${gameId}`;
      try {
        ws = new WebSocket(wsUrl);
      } catch (e) {
        scheduleReconnect();
        return;
      }
      ws.onmessage = (ev) => {
        let msg;
        try { msg = JSON.parse(ev.data); } catch (e) { return; }
        const data = msg.data;
        if (data && data.id === gameId) {
          setGame(prev => ({ ...(prev || {}), ...data }));
          if (!data.is_running) {
            setLocalTime(data.current_time || 0);
          }
        } else {
          // Messages without data (e.g. graphic_clear) - re-fetch
          fetchGame();
        }
      };
      ws.onclose = () => { ws = null; scheduleReconnect(); };
      ws.onerror = () => { try { ws && ws.close(); } catch (e) { /* ignore */ } };
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      clearTimeout(reconnectTimer);
      reconnectTimer = setTimeout(connect, 2000);
    };

    connect();

    // Low-frequency fallback poll in case WS misses an event
    pollRef.current = setInterval(() => {
      fetchGame();
    }, 15000);

    return () => {
      cancelled = true;
      clearTimeout(reconnectTimer);
      if (pollRef.current) clearInterval(pollRef.current);
      try { ws && ws.close(); } catch (e) { /* ignore */ }
    };
  }, [fetchGame, gameId]);

  // Timer logic - server-authoritative. Compute display time from
  // timer_started_at + timer_base_time so the control panel never drifts
  // even when the tab is backgrounded.
  useEffect(() => {
    if (!game) return;
    let raf = null;
    let cancelled = false;

    const tick = () => {
      if (cancelled) return;
      if (game.is_running && game.timer_started_at) {
        const startedAt = new Date(game.timer_started_at).getTime();
        const baseTime = game.timer_base_time || 0;
        const nowServer = Date.now() + clockOffsetRef.current;
        const elapsed = (nowServer - startedAt) / 1000;
        const newTime = game.timer_direction === 'up'
          ? baseTime + elapsed
          : Math.max(0, baseTime - elapsed);
        setLocalTime(Math.floor(newTime));
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
    };
  }, [game?.is_running, game?.timer_started_at, game?.timer_base_time, game?.timer_direction, game]);

  // Handler functions
  const handleTimerAction = async (action) => {
    setSyncing(true);
    try {
      await axios.post(`${API}/games/${gameId}/timer/${action}`, null, {
        headers: getAuthHeaders()
      });
      if (action === 'start') {
        await axios.post(`${API}/games/${gameId}/timer/sync?seconds=${localTime}`, null, {
          headers: getAuthHeaders()
        });
      }
      await fetchGame();
      toast.success(action === 'start' ? 'Timer started' : 'Timer paused');
    } catch (error) {
      toast.error(`Failed to ${action} timer`);
    } finally {
      setSyncing(false);
    }
  };

  const handleSetTime = async (seconds) => {
    setSyncing(true);
    try {
      await axios.post(`${API}/games/${gameId}/timer/set?seconds=${seconds}`, null, {
        headers: getAuthHeaders()
      });
      setLocalTime(seconds);
      toast.success('Timer set');
    } catch (error) {
      toast.error('Failed to set timer');
    } finally {
      setSyncing(false);
    }
  };

  const handleScore = async (team, scoreType, value) => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/games/${gameId}/score?team=${team}&score_type=${scoreType}&value=${value}`, null, {
        headers: getAuthHeaders()
      });
      setGame(response.data);
    } catch (error) {
      toast.error('Failed to update score');
    } finally {
      setSyncing(false);
    }
  };

  const handleTriggerEvent = async (eventType, team = 1) => {
    try {
      await axios.post(`${API}/games/${gameId}/event?event_type=${eventType}&team=${team}`, null, {
        headers: getAuthHeaders()
      });
      toast.success(`${eventType} animation triggered!`);
      setTimeout(async () => {
        await axios.post(`${API}/games/${gameId}/event/clear`, null, {
          headers: getAuthHeaders()
        });
      }, 5000);
    } catch (error) {
      toast.error('Failed to trigger event');
    }
  };

  const handleTriggerGoalGraphic = async (team, scorerData) => {
    try {
      await axios.post(`${API}/games/${gameId}/goal-graphic`, {
        team,
        scorer_name: scorerData.name,
        scorer_number: scorerData.number,
        minute: scorerData.minute
      }, {
        headers: getAuthHeaders()
      });
      toast.success(`Goal graphic triggered - ${scorerData.name}!`);
      // Refresh game data to get updated active_goal
      fetchGame();
    } catch (error) {
      console.error('Goal graphic error:', error.response?.data || error.message);
      toast.error('Failed to trigger goal graphic');
    }
  };

  const handleCard = async (team, cardType, action) => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/games/${gameId}/card?team=${team}&card_type=${cardType}&action=${action}`, null, {
        headers: getAuthHeaders()
      });
      setGame(response.data);
    } catch (error) {
      toast.error('Failed to update card');
    } finally {
      setSyncing(false);
    }
  };

  const handleSetPeriod = async (period) => {
    setSyncing(true);
    try {
      let startTimeSeconds = null;
      
      // Default start times for each period
      const defaultStartTimes = {
        '1st_half': 0,        // 0:00
        '2nd_half': 2700,     // 45:00
        '1st_et': 5400,       // 90:00
        '2nd_et': 6300,       // 105:00
        'fulltime': 5400,     // 90:00 (show 90 at fulltime)
      };
      
      // Check if user has custom start time configured
      if (periodStartTimes[period]) {
        const timeStr = periodStartTimes[period];
        const parts = timeStr.split(':');
        if (parts.length === 2) {
          startTimeSeconds = parseInt(parts[0]) * 60 + parseInt(parts[1]);
        } else if (parts.length === 1) {
          startTimeSeconds = parseInt(parts[0]) * 60;
        }
      } else if (defaultStartTimes[period] !== undefined) {
        // Use default start time if no custom time set
        startTimeSeconds = defaultStartTimes[period];
      }
      
      let url = `${API}/games/${gameId}/period?period=${period}`;
      if (startTimeSeconds !== null && !isNaN(startTimeSeconds)) {
        url += `&start_time=${startTimeSeconds}`;
      }
      
      const response = await axios.post(url, null, {
        headers: getAuthHeaders()
      });
      
      setGame(prev => prev ? {
        ...prev, 
        period,
        current_time: response.data.current_time ?? prev.current_time,
        is_running: response.data.is_running ?? prev.is_running
      } : prev);
      
      if (response.data.current_time !== undefined) {
        setLocalTime(response.data.current_time);
      }
      
      const periodLabels = {
        'pregame': 'PRE-GAME', '1st_half': '1ST HALF', 'halftime': 'HALF TIME',
        '2nd_half': '2ND HALF', '1st_et': '1ST ET', 'halftime_et': 'HT (ET)',
        '2nd_et': '2ND ET', 'fulltime': 'FULL TIME'
      };
      
      const isPlayingPeriod = ['1st_half', '2nd_half', '1st_et', '2nd_et'].includes(period);
      toast.success(`${periodLabels[period]}${isPlayingPeriod ? ' - Timer Started!' : ''}`);
    } catch (error) {
      toast.error('Failed to set period');
    } finally {
      setSyncing(false);
    }
  };

  const handleUpdateStoppage = async (minutes) => {
    setSyncing(true);
    try {
      const newStoppage = Math.max(0, (game?.stoppage_time || 0) + minutes);
      await axios.patch(`${API}/games/${gameId}`, { stoppage_time: newStoppage }, {
        headers: getAuthHeaders()
      });
      setGame(prev => prev ? {...prev, stoppage_time: newStoppage} : prev);
    } catch (error) {
      toast.error('Failed to update stoppage time');
    } finally {
      setSyncing(false);
    }
  };

  const handleUpdateTeamName = async (team, name) => {
    try {
      const teamKey = `team${team}`;
      const teamData = { ...game[teamKey], name };
      await axios.patch(`${API}/games/${gameId}`, { [teamKey]: teamData }, {
        headers: getAuthHeaders()
      });
      await fetchGame();
    } catch (error) {
      toast.error('Failed to update team name');
    }
  };

  const handleUpdateCompetition = async (name) => {
    try {
      await axios.patch(`${API}/games/${gameId}`, { competition_name: name }, {
        headers: getAuthHeaders()
      });
      setGame(prev => prev ? {...prev, competition_name: name} : prev);
    } catch (error) {
      toast.error('Failed to update competition name');
    }
  };

  const handleUpdateMatchInfo = async (field, value) => {
    try {
      await axios.patch(`${API}/games/${gameId}`, { [field]: value }, {
        headers: getAuthHeaders()
      });
      setGame(prev => prev ? {...prev, [field]: value} : prev);
    } catch (error) {
      toast.error('Failed to update match info');
    }
  };

  const handleAddMatchEvent = async (eventData) => {
    try {
      await axios.post(`${API}/games/${gameId}/match-event`, eventData, {
        headers: getAuthHeaders()
      });
      toast.success('Event recorded!');
      fetchMatchEvents();
    } catch (error) {
      toast.error('Failed to add event');
    }
  };

  const handleDeleteMatchEvent = async (eventId) => {
    try {
      await axios.delete(`${API}/games/${gameId}/match-event/${eventId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Event deleted');
      fetchMatchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  // Utility functions
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatGAAScore = (team) => {
    const sportConfig = SPORT_CONFIG[game?.sport];
    if (sportConfig?.hasCombinedScore) {
      return `${team.goals || 0}-${team.points || 0}`;
    }
    return team.score || 0;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="text-white font-body">Loading...</div>
      </div>
    );
  }

  if (!game) return null;

  const sportConfig = SPORT_CONFIG[game.sport];

  return (
    <div className="min-h-screen bg-[#0F172A] p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={() => navigate('/')}
          className="text-slate-400 hover:text-white"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>
        <div className="flex items-center gap-2">
          <Button
            data-testid="toggle-preview-btn"
            variant="ghost"
            onClick={() => setShowPreview(!showPreview)}
            className={`text-xs px-3 py-1 ${showPreview ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300'}`}
          >
            {showPreview ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
            {showPreview ? 'Hide' : 'Show'} Preview
          </Button>
          {syncing && <RefreshCw className="w-4 h-4 text-blue-400 animate-spin" />}
          <div className="flex items-center gap-2 px-3 py-1 rounded bg-green-900/30 text-green-400">
            <div className="w-2 h-2 rounded-full bg-green-400" />
            <span className="text-xs font-body">READY</span>
          </div>
        </div>
      </div>

      {/* Live Preview Panel */}
      {showPreview && (
        <Card className={`bg-[#0a0f1a] border-slate-700 mb-4 overflow-hidden transition-all ${previewExpanded ? 'fixed inset-4 z-50' : ''}`}>
          <div className="flex items-center justify-between px-3 py-2 bg-[#1E293B] border-b border-slate-700">
            <span className="text-white font-body text-sm flex items-center gap-2">
              <Eye className="w-4 h-4 text-blue-400" />
              LIVE PREVIEW
              <span className="text-xs text-slate-500">(auto-refreshes every 1s)</span>
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => previewRef.current?.contentWindow?.location.reload()}
                className="text-slate-400 hover:text-white h-7 px-2"
              >
                <RefreshCw className="w-3 h-3" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPreviewExpanded(!previewExpanded)}
                className="text-slate-400 hover:text-white h-7 px-2"
              >
                {previewExpanded ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
              </Button>
              {previewExpanded && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setPreviewExpanded(false); setShowPreview(false); }}
                  className="text-slate-400 hover:text-white h-7 px-2"
                >
                  ✕
                </Button>
              )}
            </div>
          </div>
          <div className={`relative bg-[#1a1a2e] ${previewExpanded ? 'h-[calc(100%-44px)]' : 'h-48 md:h-64'}`}>
            <iframe
              ref={previewRef}
              src={`${process.env.REACT_APP_BACKEND_URL}/api/overlay-html/${gameId}`}
              className="w-full h-full border-0"
              title="Live Overlay Preview"
              style={{ 
                transform: previewExpanded ? 'scale(1)' : 'scale(0.5)', 
                transformOrigin: 'top left',
                width: previewExpanded ? '100%' : '200%',
                height: previewExpanded ? '100%' : '200%'
              }}
            />
            {!previewExpanded && (
              <div className="absolute bottom-2 right-2 text-xs text-slate-500 bg-black/50 px-2 py-1 rounded">
                50% scale • Click expand for full size
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Score Display */}
      <Card className="bg-[#1E293B] border-slate-700 mb-4">
        <CardContent className="p-4">
          <div className="text-center mb-2">
            <span className="text-xs font-body uppercase tracking-widest text-[#D4E5ED]">
              {sportConfig?.name} {game.competition_name && `• ${game.competition_name}`}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <div className="text-center flex-1">
              <p className="font-heading text-2xl md:text-3xl text-white">{game.team1.name}</p>
              <p className="font-mono text-5xl md:text-6xl text-[#D4E5ED] font-bold">
                {formatGAAScore(game.team1)}
              </p>
              {sportConfig?.hasCombinedScore && (
                <p className="text-slate-400 font-mono">({game.team1.score})</p>
              )}
            </div>
            <div className="px-4">
              <p className="font-mono text-3xl text-slate-500">-</p>
            </div>
            <div className="text-center flex-1">
              <p className="font-heading text-2xl md:text-3xl text-white">{game.team2.name}</p>
              <p className="font-mono text-5xl md:text-6xl text-[#D4E5ED] font-bold">
                {formatGAAScore(game.team2)}
              </p>
              {sportConfig?.hasCombinedScore && (
                <p className="text-slate-400 font-mono">({game.team2.score})</p>
              )}
            </div>
          </div>
          {/* Timer */}
          <div className="text-center mt-4">
            <div className="inline-flex items-center gap-2 bg-[#0F172A] px-6 py-3 rounded-lg">
              <Clock className="w-5 h-5 text-slate-400" />
              <span className="font-mono text-4xl text-white">{formatTime(localTime)}</span>
              {game.stoppage_time > 0 && (
                <span className="text-[#F97316] font-mono text-2xl ml-2">+{game.stoppage_time}'</span>
              )}
            </div>
            <p className="text-slate-500 text-xs mt-1 font-body">
              {game.timer_direction === 'up' ? 'Counting Up' : 'Counting Down'} • {(game.period || 'pregame').replace('_', ' ').toUpperCase()}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Control Tabs */}
      <Tabs defaultValue="timer" className="w-full">
        <TabsList className="grid grid-cols-7 bg-[#1E293B] mb-4">
          <TabsTrigger value="timer" className="font-body data-[state=active]:bg-[#2A4A5C] text-xs sm:text-sm">Timer</TabsTrigger>
          <TabsTrigger value="period" className="font-body data-[state=active]:bg-[#2A4A5C] text-xs sm:text-sm">Period</TabsTrigger>
          <TabsTrigger value="score" className="font-body data-[state=active]:bg-[#2A4A5C] text-xs sm:text-sm">Score</TabsTrigger>
          <TabsTrigger value="events" className="font-body data-[state=active]:bg-[#2A4A5C] text-xs sm:text-sm">Events</TabsTrigger>
          <TabsTrigger value="cards" className="font-body data-[state=active]:bg-[#2A4A5C] text-xs sm:text-sm">Cards</TabsTrigger>
          <TabsTrigger value="graphics" className="font-body data-[state=active]:bg-[#2A4A5C] text-xs sm:text-sm">Graphics</TabsTrigger>
          <TabsTrigger value="settings" className="font-body data-[state=active]:bg-[#2A4A5C] text-xs sm:text-sm">More</TabsTrigger>
        </TabsList>

        <TabsContent value="timer">
          <TimerControls
            game={game}
            localTime={localTime}
            syncing={syncing}
            onTimerAction={handleTimerAction}
            onSetTime={handleSetTime}
            onUpdateStoppage={handleUpdateStoppage}
          />
        </TabsContent>

        <TabsContent value="period">
          <PeriodControls
            game={game}
            syncing={syncing}
            periodStartTimes={periodStartTimes}
            onPeriodStartTimesChange={(period, value) => 
              setPeriodStartTimes(prev => ({...prev, [period]: value}))
            }
            onSetPeriod={handleSetPeriod}
          />
        </TabsContent>

        <TabsContent value="score">
          <ScoreControls
            game={game}
            syncing={syncing}
            onScore={handleScore}
            onTriggerEvent={handleTriggerEvent}
            onTriggerGoalGraphic={handleTriggerGoalGraphic}
          />
        </TabsContent>

        <TabsContent value="events">
          <EventsPanel
            game={game}
            localTime={localTime}
            matchEvents={matchEvents}
            onAddEvent={handleAddMatchEvent}
            onDeleteEvent={handleDeleteMatchEvent}
          />
        </TabsContent>

        <TabsContent value="cards">
          <CardsPanel
            game={game}
            syncing={syncing}
            onCard={handleCard}
            gameId={gameId}
            getAuthHeaders={getAuthHeaders}
            currentGameTime={localTime}
          />
        </TabsContent>

        <TabsContent value="graphics" className="max-h-[calc(100vh-400px)] overflow-y-auto">
          <GraphicsPanel
            game={game}
            gameId={gameId}
            getAuthHeaders={getAuthHeaders}
            onGameUpdate={fetchGame}
          />
        </TabsContent>

        <TabsContent value="settings">
          <SettingsPanel
            game={game}
            gameId={gameId}
            lineupStatus={lineupStatus}
            onUpdateCompetition={handleUpdateCompetition}
            onUpdateTeamName={handleUpdateTeamName}
            onUpdateMatchInfo={handleUpdateMatchInfo}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Control;
